/**
 * Shapes of the four source kinds a widget can point at. The catalog itself
 * (`catalog.ts`) assembles these from the per-domain files in `sources/`.
 */

import type { DatasetBag, DatasetId } from './datasets';
import type { Aggregate } from './series';
import type { ForecastPoint, MetricDirection, SeriesPoint, Unit } from './types';

export type SourceGroup =
  | 'Finance'
  | 'Sales'
  | 'Operations'
  | 'Inventory'
  | 'Procurement'
  | 'Marketing'
  | 'Projects'
  | 'Tracked KPIs';

export const SOURCE_GROUP_ORDER: SourceGroup[] = [
  'Finance',
  'Sales',
  'Operations',
  'Inventory',
  'Procurement',
  'Marketing',
  'Projects',
  'Tracked KPIs',
];

interface SourceBase {
  id: string;
  label: string;
  group: SourceGroup;
  description: string;
  /** Permission module gate — `canRead(role, module)`. */
  module: string;
  requires: DatasetId[];
}

/**
 * A metric is a single number over a window.
 *
 *   day / month   a dated series; the window value is `agg` over the points
 *                 inside it, and the previous window gives the delta
 *   snapshot      the current state only (open POs, cash today); no history
 *   window        computed from the raw rows of a window (repeat-customer
 *                 rate) — supports deltas but not sparklines
 */
export interface MetricDef extends SourceBase {
  kind: 'metric';
  unit: Unit;
  direction: MetricDirection;
  grain: 'day' | 'month' | 'snapshot' | 'window';
  agg: Aggregate;
  series?: (bag: DatasetBag) => SeriesPoint[];
  /** Window value = value(num) / value(den), both evaluated for the same window. */
  ratio?: { num: string; den: string };
  snapshot?: (bag: DatasetBag) => number | null;
  windowValue?: (bag: DatasetBag, start: string, end: string) => number | null;
  /** When false the metric renders an explanatory "not available" state. */
  available?: (bag: DatasetBag) => string | null;
  /** Forecast-friendly: values can't go below zero (counts, revenue). */
  nonNegative?: boolean;
}

export interface BreakdownSlice {
  label: string;
  value: number;
}

/** Category totals for a window (revenue by channel, spend by vendor, …). */
export interface BreakdownDef extends SourceBase {
  kind: 'breakdown';
  unit: Unit;
  slices: (bag: DatasetBag, start: string, end: string) => BreakdownSlice[];
}

export type TableCellValue = string | number | null;

export interface TableColumnDef {
  key: string;
  label: string;
  unit?: Unit;
  align?: 'left' | 'right';
  /** Treat as a date-only ISO string. */
  date?: boolean;
  /** Tone a numeric cell by sign. */
  signed?: boolean;
}

export interface TableRowData {
  key: string;
  cells: Record<string, TableCellValue>;
  /** Optional in-app link for the row. */
  href?: string;
}

export interface TableDef extends SourceBase {
  kind: 'table';
  columns: TableColumnDef[];
  rows: (bag: DatasetBag, start: string, end: string) => TableRowData[];
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  defaultLimit?: number;
  /** Column keys shown by default (others hidden until enabled). */
  defaultColumns?: string[];
}

/**
 * A forecastable series. History is always month grain so every model on
 * the Forecast page compares like with like.
 */
export interface ForecastSourceDef extends SourceBase {
  kind: 'forecast';
  unit: Unit;
  nonNegative: boolean;
  /** Requires `itemId` in the widget config. */
  perItem?: boolean;
  /**
   * How to fill months with no data point: flows (revenue, orders) are
   * genuinely zero, levels (followers, balances) carry the last value.
   */
  fill?: 'zero' | 'carry';
  /** Monthly actuals — complete and partial months alike; evaluation trims. */
  history: (bag: DatasetBag, itemId?: string | null) => SeriesPoint[];
  /** Optional budget / manual projection by month. */
  manual?: (bag: DatasetBag, itemId?: string | null) => SeriesPoint[];
  manualLabel?: string;
  /** Optional precomputed forecast (e.g. the Edge Function demand run). */
  external?: (bag: DatasetBag, itemId?: string | null) => ForecastPoint[];
  externalLabel?: string;
  /**
   * Cash-style sources project a *balance*: the model forecasts the
   * per-period change and the balance is walked forward from `startValue`.
   */
  balance?: {
    startValue: (bag: DatasetBag) => number | null;
    flow: (bag: DatasetBag) => SeriesPoint[];
  };
}

export type SourceDef = MetricDef | BreakdownDef | TableDef | ForecastSourceDef;
